import AddressSearchStatus from "../../components/Address/AddressSearchStatus";
import { useCallback, useEffect, useRef, useState } from "react";
import useMarketPlacesAutocomplete from "../../hooks/useMarketPlacesAutocomplete";
import styled from "styled-components";
import Navbar from "../../components/Navbar";
import SavedAddressPicker from "../../components/Address/SavedAddressPicker";
import CheckoutStepper from "../../components/CheckoutStepper";
import { saveShippingAddress } from "../../redux/cartRedux";
import { useDispatch, useSelector } from "react-redux";
import { getDeliveryZones } from "../../redux/addressAction";
import { useHistory } from "react-router-dom";
import { Container, Wrapper } from "../../components/Layout/checkoutLayout";
import {
  GoogleMap,
  InfoWindow,
} from "@react-google-maps/api";

import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";

import mapStyles from "./mapStyles";
import { isWithinMarketBounds, MAP_CENTER, normalizeMarketLatLng } from "../../config/market";
import useCoverage from "../../redux/useCoverage";
import CoverageBanner from "../../components/CoverageBanner";
import GoogleMapBoundary from "../../components/Maps/GoogleMapBoundary";
import MapPin from "../../components/Maps/MapPin";
import { geocodeAddress, getPlaceSummary, latLngFromGeocode } from "../../utils/googlePlaces";
import reportClientError from "../../utils/clientTelemetry";
import {
  evaluateShippingGate,
  isValidDeliveryPhone,
  savedAddressLatLng,
  shippingGateLabel,
} from "../../utils/shippingValidation";
import { resolveInitialDeliveryPhone, saveLastDeliveryPhone } from "../../utils/deliveryPhone";

const Button = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

// Page background + card chrome are shared — see components/Layout/checkoutLayout.

const Title = styled.h1`
  font-size: 24px;
  font-weight: 300;
`;
const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
`;
const Input = styled.input`
  flex: 1;
  min-width: 40%;
  margin: 10px 0px;
  padding: 10px;
  border: 1px solid red;
  border-radius: 8px;
  outline: none;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.9);
  background-color: #e2e2e2c4;
`;

// const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
};
const options = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: mapStyles,
};

const isDevelopment = process.env.NODE_ENV === "development";
const devAddress = { value: "Kampala, Uganda" };

const ShippingScreen = () => {
  // Google maps
  const {
    ready,
    value,
    suggestions: { data, loading: addressLoading, status: addressStatus },
    setValue,
    clearSuggestions,
    error: placesError,
  } = useMarketPlacesAutocomplete({ debounce: 100 });

  const [customerNumber, setCustomerNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locationName, setLocationName] = useState([]);
  const [latLng, setLatLng] = useState(MAP_CENTER);
  const [hasChosenAddress, setHasChosenAddress] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
  const timeoutRef = useRef(null);

  const [noResultsMessage, setNoResultsMessage] = useState("");
  const typingTimer = useRef();

  const dispatch = useDispatch();
  const history = useHistory();
  const products = useSelector((state) => state.cart.products);
  const currentUser = useSelector((state) => state.user?.currentUser);
  const userLocation = useSelector((state) => state.userLocation);

    // An EXPLICIT pick outranks the saved primary. `locationSource:'manual'` is
    // set only by setManualAddress — the shopper typed and chose a place. A
    // gps/ip guess is a much weaker signal about where they want a delivery, so
    // the saved primary still wins there.
    const browseLocationIsExplicit =
        userLocation?.locationSource === "manual" &&
        isWithinMarketBounds(userLocation?.latitude, userLocation?.longitude);
  const rawVendorId = products?.[0]?.vendorId;
  const vendorId = rawVendorId?._id || rawVendorId;
  const coverage = useCoverage({
    vendorId,
    vendorType: "inhouse",
    enabled: hasChosenAddress,
    lat: latLng?.lat,
    lng: latLng?.lng,
  });

  useEffect(() => {
    dispatch(getDeliveryZones());

    // Set dev address on mount in development mode
    if (isDevelopment) {
      setValue(devAddress.value, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const phonePrefilledRef = useRef(false);
  useEffect(() => {
    if (phonePrefilledRef.current) return;
    const prefill = resolveInitialDeliveryPhone(currentUser);
    if (prefill) {
      phonePrefilledRef.current = true;
      setCustomerNumber(String(prefill));
    }
  }, [currentUser]);

  const locationPrefilledRef = useRef(false);
  useEffect(() => {
    if (locationPrefilledRef.current || hasChosenAddress) return;
    const coordinates = normalizeMarketLatLng(userLocation?.latitude, userLocation?.longitude);
    if (!coordinates) return;
    locationPrefilledRef.current = true;
    const label = userLocation?.address || "Your set location";
    setLatLng(coordinates);
    setLocationName({ name: label, vicinity: label });
    setValue(label, false);
    setHasChosenAddress(true);
  }, [
    hasChosenAddress,
    setValue,
    userLocation?.address,
    userLocation?.latitude,
    userLocation?.longitude,
  ]);

  useEffect(() => {
    if (placesError) {
      setAddressError(
        "Address search is temporarily unavailable. Retry the map or use a valid saved address."
      );
    }
  }, [placesError]);

  useEffect(() => {
    if (locationId) {
      getPlaceSummary(locationId)
        .then((res) => {
          setLocationName((current) => ({
            name: res.name || current?.name || "",
            vicinity: res.vicinity || current?.vicinity || "",
          }));
        })
        .catch((err) => {
          reportClientError(err, { area: "inhouse_place_details" });
        });
    }
  }, [locationId]);

  // Shows searching if typing and Address not found if none is found and there's a delay of 3 seconds
  useEffect(() => {
    if (fetchingSuggestions) {
      clearTimeout(typingTimer.current);
      setNoResultsMessage("Searching...");
    } else {
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        if (data.length === 0 && value.length > 0 && !locationName.name) {
          setNoResultsMessage("Address not found!");
        } else {
          setNoResultsMessage("");
        }
      }, 2000); // 3 seconds delay
    }
  }, [data, fetchingSuggestions, value, locationName]);

  // Pre-fill the manual form from a chosen saved address so its latLng flows
  // into saveShippingAddress (delivery coverage + fee read shippingAddress.latLng).
  const handleSelectSavedAddress = (addr) => {
    if (!addr) return;
    const coordinates = savedAddressLatLng(addr);
    if (!coordinates) {
      setHasChosenAddress(false);
      setAddressError("This saved address needs an updated location pin before checkout.");
      return;
    }
    if (addr.recipientPhone) {
      setCustomerNumber(addr.recipientPhone);
      setPhoneError("");
    }
    setAddressError("");
    setLocationName({ name: addr.label || "Saved address", vicinity: addr.address || addr.description || "" });
    setLatLng(coordinates);
    setHasChosenAddress(true);
    setLocationId("");
    setValue(addr.address || "", false);
  };

  // const selectedVillage = selectVillage?.value;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!shippingGate.canContinue) return;
    saveLastDeliveryPhone(customerNumber);
    dispatch(
      saveShippingAddress({
        customerNumber,
        // selectedVillage,
        locationName, // locationName: {name: res.name, vicinity: res.vicinity}
        latLng, // latLng
        source: "places", // pin is the geocoder point for the picked address, not a GPS fix
        locationId, // Delivery address locationId
      })
    );
    history.push("/payment");
  };

  const boxOptionRef = useRef();

  const panTo = useCallback(({ lat, lng }) => {
    // console.log(lat, lng, " lat lng");
  }, []);

  const handleSelect = async (value) => {
    setValue(value, false);
    clearSuggestions();

    try {
      const results = await geocodeAddress(value);
      const raw = latLngFromGeocode(results[0]);
      const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!coordinates) throw new Error("The selected address is outside this delivery market");
      setLocationId(results[0].place_id);
      panTo(coordinates);
      setLatLng(coordinates);
      setLocationName({ name: value, vicinity: "" });
      setHasChosenAddress(true);
      setAddressError("");
    } catch (error) {
      reportClientError(error, { area: "inhouse_shipping_geocode" });
      setHasChosenAddress(false);
      setAddressError("We couldn’t pin that address. Choose another suggestion and retry.");
    }
  };

  const handleInput = (e) => {
    setValue(e.target.value);
    setFetchingSuggestions(true);
    setAddressError("");

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      setFetchingSuggestions(false);
    }, 100);
  };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;
      return (
        <ComboboxOption
          key={place_id}
          value={main_text + " " + secondary_text}
        />
      );
    });

  // Get details about location ID

  // Clear suggestions
  const clearRenderedLocationDetails = () => {
    clearSuggestions();
    setLatLng(MAP_CENTER);
    setValue("");
    setLocationId("");
    setLocationName("");
    setHasChosenAddress(false);
    setAddressError("");
  };

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setCustomerNumber(phone);

    if (!isValidDeliveryPhone(phone)) {
      setPhoneError(
        "*Phone number must be atleast 10 digits long [e.g 0700123123]"
      );
    } else {
      setPhoneError("");
    }
  };

  const shippingGate = evaluateShippingGate({
    phone: customerNumber,
    address: locationName?.name,
    lat: latLng?.lat,
    lng: latLng?.lng,
    hasChosenAddress,
    vendorId,
    coverage,
  });

  return (
    <>
      <Navbar />
      <Container>
        <Wrapper>
          <div style={{ marginBottom: '20px', marginTop: '30px' }}>
            <CheckoutStepper currentStep="shipping" />
          </div>
          <Title>SHIPPING ADDRESS</Title>
          <SavedAddressPicker
            autoSelect={!browseLocationIsExplicit}
            onSelect={handleSelectSavedAddress}
            onDeselect={clearRenderedLocationDetails}
          />
          {isDevelopment && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '5px',
              padding: '8px 12px',
              marginBottom: '10px',
              fontSize: '12px',
              color: '#856404',
              textAlign: 'center'
            }}>
              🛠️ <strong>Development Mode:</strong> Form pre-filled with dummy data
            </div>
          )}
          <Form>
            {/* Number Input */}
            <Input
              value={customerNumber}
              // onChange={(e) => setCustomerNumber(e.target.value)}
              onChange={handlePhoneChange}
              placeholder="Enter Contact Phone number..."
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
            />
            {phoneError && (
              <p
                style={{
                  color: "red",
                  fontSize: "10px",
                  margin: "0",
                  padding: "0",
                  textAlign: "left",
                  fontStyle: "italic",
                }}
              >
                {phoneError}
              </p>
            )}
            {/* Address Input With Google Map */}
            <GoogleMapBoundary height="400px">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={12}
                center={latLng}
                options={options}
              >
              {/* Combobox */}
              <div
                style={{
                  display: "flex",
                  alignSelf: "center",
                  flexDirection: "row",
                  marginTop: "5px",
                }}
              >
                <div
                  style={{
                    width: "80%",
                    zIndex: "3",
                    margin: "0",
                  }}
                >
                  <Combobox ref={boxOptionRef} onSelect={handleSelect}>
                    <div style={{ position: "relative" }}>
                      <ComboboxInput
                        value={value}
                        onChange={handleInput}
                        disabled={!ready}
                        placeholder={ready ? "Enter Delivery Address..." : "Loading address search…"}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #f58c60",
                          borderRadius: "5px",
                          zIndex: "3",
                          boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.5)",
                          backgroundColor: "#e2e2e2c4",
                          outline: "none",
                        }}
                      />
                      {fetchingSuggestions ||
                        (data.length === 0 && value.length > 0) ? (
                        <>
                          <p
                            style={{
                              color: "red",
                              fontSize: "11px",
                              fontStyle: "italic",
                              border: "1px solid #ccc",
                              backgroundColor: "#f8f8f8",
                              borderRadius: "4px",
                              padding: "5px",
                              width: "100%",
                            }}
                          >
                            {noResultsMessage}
                          </p>
                        </>
                      ) : null}
                    </div>
                    <ComboboxPopover
                      style={{
                        backgroundColor: "white",
                        margin: "0",
                        padding: "0",
                        fontSize: "12px",
                        border: "1px solid #F58C60",
                        fontStyle: "italic",
                        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.8)",
                        borderBottomRightRadius: "10px",
                        borderBottomLeftRadius: "10px",
                      }}
                    >
                      <ComboboxList
                        style={{
                          marginLeft: "10px",
                          padding: "0",
                          // listStyle: "none",
                          overflow: "auto",
                          maxHeight: "200px",
                          lineHeight: "2",
                        }}
                      >
                        {renderSuggestions()}
                      </ComboboxList>
                    </ComboboxPopover>
                  </Combobox>
                  <AddressSearchStatus loading={addressLoading} ready={ready} status={addressStatus} count={data.length} error={placesError} />
                  {addressError && (
                    <p role="alert" style={{ color: "#b3362a", fontSize: "11px" }}>
                      {addressError}
                    </p>
                  )}
                </div>
              </div>

              {hasChosenAddress && <MapPin position={latLng} label="Delivery location" />}
              {hasChosenAddress && locationName?.name && (
                <InfoWindow
                  position={latLng}
                  onCloseClick={() => clearRenderedLocationDetails()}
                >
                  <div
                    style={{
                      paddingBottom: "5px",
                    }}
                  >
                    <h3
                      style={{
                        color: "orange",
                        fontSize: "15px",
                        fontWeight: "bold",
                      }}
                    >
                      <span role="img" aria-label="bear">
                        {/* 🐻 */}
                        {/* 📍 */}
                        📌
                      </span>{" "}
                      Set Delivery Address
                    </h3>
                    <p
                      style={{
                        textAlign: "center",
                        fontStyle: "italic",
                        fontSize: "10px",
                      }}
                    >
                      <span>
                        {locationName.name} ~ {locationName.vicinity}
                      </span>
                    </p>
                  </div>
                </InfoWindow>
              )}
              </GoogleMap>
            </GoogleMapBoundary>

            <CoverageBanner
              variant="block"
              deliverable={coverage.deliverable}
              reason={coverage.reason}
              distanceKm={coverage.distanceKm}
              radius={coverage.radius}
              hasLocation={coverage.hasLocation}
              status={coverage.status}
              error={coverage.error}
              onRetry={coverage.refresh}
            />
            <Button
              disabled={!shippingGate.canContinue}
              onClick={handleSubmit}
            >
              {shippingGateLabel(shippingGate.code)}
            </Button>
          </Form>
        </Wrapper>
      </Container>
    </>
  );
};

export default ShippingScreen;
